/**
 * Reads the Class column off an imported roster.
 *
 * Rosters arrive as whatever the school's SID typed, and there is no house style across them:
 * one lists "Freshman", the next "FR", the next "R-Fr.", the next "1". Three separate readers
 * used to disagree about which of those meant what, so the same file could be read one way by
 * the pipe parser and another by the review grid. This is the one place that decides.
 *
 * ONE CELL, TWO LADDERS. A Class cell answers two different questions and the answers disagree:
 *
 *   - eligibilityClass (1-5) counts years of competition eligibility. A redshirt year consumed
 *     none, so "RS Fr." is still a 1.
 *   - jucoYear (1-3) counts years in the program. A redshirt year absolutely counts, so
 *     "RS Fr." is JC 2.
 *
 * Neither is wrong, and neither is derivable from the other — which is why the lookup resolves a
 * cell to (rung, redshirt, form) and emits both, rather than mapping straight to a number.
 *
 * Mirrors RosterClassParser on the API and mobile/utils/roster-class.ts. All three have to agree
 * or a roster reads as one class on upload and another on screen — keep the family table, the
 * redshirt rule, both ladders and the June rollover in step with them.
 */

/** A year of eligibility. Mirrors SCHOLARSHIP_CLASSES and ScholarshipPlayerClasses.All. */
export type EligibilityClass = '1' | '2' | '3' | '4' | '5';

export const ELIGIBILITY_CLASSES: readonly EligibilityClass[] = ['1', '2', '3', '4', '5'];

/** A year of junior college. There is no JC 4 — see jucoYearFor. */
export type JucoYear = '1' | '2' | '3';

export const JUCO_YEARS: readonly JucoYear[] = ['1', '2', '3'];

export interface RosterClassReading {
  /** The year of eligibility the cell named, or null when it named a graduation year instead. */
  eligibilityClass: EligibilityClass | null;
  /** The year of junior college the cell named, or null when the cell cannot name one. */
  jucoYear: JucoYear | null;
  /** The graduation year the cell stated, or the one its year of eligibility implies. */
  gradYear: number | null;
}

const EMPTY_READING: RosterClassReading = {
  eligibilityClass: null,
  jucoYear: null,
  gradYear: null,
};

// Century pinned so a fat-fingered 1027 is not read as a class. Keep in sync with
// isGraduationYearClass, ck_scholarship_players_class and the API's GraduationYearPattern.
const GRADUATION_YEAR_PATTERN = /^20\d{2}$/;
const TWO_DIGIT_YEAR_PATTERN = /^\d{2}$/;

/**
 * How a cell names its rung.
 *
 * "academic" is four-year-school vocabulary — Fr, So, Jr, Sr. "ordinal" counts positions — 1,
 * 1st, first. They sit on the same eligibility rung but say different things on a JUCO roster: a
 * cell reading "3" means a third-year player, while "Jr" is vocabulary that does not belong on a
 * two-year roster at all and is worth a human look. See jucoYearFor.
 */
type ClassForm = 'academic' | 'ordinal';

interface ClassFamily {
  /** 1-5. The year of eligibility, before any redshirt adjustment (there is none). */
  rung: 1 | 2 | 3 | 4 | 5;
  form: ClassForm;
}

/**
 * Every spelling of a class we have seen on a real roster, resolved to its rung and its form.
 *
 * Keys are already normalized — lowercased with every separator removed — so one entry covers a
 * whole family: "fr" is what "FR", "Fr." and "fr" all collapse to. Misspellings common enough to
 * matter are listed outright ("sophmore"), because nobody spellchecks a roster before uploading
 * it.
 *
 * REDSHIRT SPELLINGS ARE NOT LISTED HERE. They are handled by stripRedshirt, which is what lets
 * "RS Fr." work at all — see the note there. Listing them outright is how "RS Fr." came to parse
 * as nothing while "R-Fr" parsed fine.
 *
 * Two single letters are deliberately absent. Bare "S" is unreadable — sophomore and senior both
 * claim it. Bare "RF" is left to right field, which owns it on any roster listing positions;
 * stripRedshirt's two-character rule is what keeps it that way.
 */
const CLASS_FAMILIES: Record<string, ClassFamily> = {
  // First year
  f: { rung: 1, form: 'academic' },
  fr: { rung: 1, form: 'academic' },
  fre: { rung: 1, form: 'academic' },
  fresh: { rung: 1, form: 'academic' },
  frosh: { rung: 1, form: 'academic' },
  freshman: { rung: 1, form: 'academic' },
  freshmen: { rung: 1, form: 'academic' },
  '1': { rung: 1, form: 'ordinal' },
  '1st': { rung: 1, form: 'ordinal' },
  '1styear': { rung: 1, form: 'ordinal' },
  first: { rung: 1, form: 'ordinal' },
  firstyear: { rung: 1, form: 'ordinal' },

  // Second year
  so: { rung: 2, form: 'academic' },
  sop: { rung: 2, form: 'academic' },
  soph: { rung: 2, form: 'academic' },
  sophomore: { rung: 2, form: 'academic' },
  sophomores: { rung: 2, form: 'academic' },
  sophmore: { rung: 2, form: 'academic' },
  '2': { rung: 2, form: 'ordinal' },
  '2nd': { rung: 2, form: 'ordinal' },
  '2ndyear': { rung: 2, form: 'ordinal' },
  second: { rung: 2, form: 'ordinal' },
  secondyear: { rung: 2, form: 'ordinal' },

  // Third year
  j: { rung: 3, form: 'academic' },
  jr: { rung: 3, form: 'academic' },
  jun: { rung: 3, form: 'academic' },
  junior: { rung: 3, form: 'academic' },
  juniors: { rung: 3, form: 'academic' },
  '3': { rung: 3, form: 'ordinal' },
  '3rd': { rung: 3, form: 'ordinal' },
  '3rdyear': { rung: 3, form: 'ordinal' },
  third: { rung: 3, form: 'ordinal' },
  thirdyear: { rung: 3, form: 'ordinal' },

  // Fourth year
  sr: { rung: 4, form: 'academic' },
  sen: { rung: 4, form: 'academic' },
  senior: { rung: 4, form: 'academic' },
  seniors: { rung: 4, form: 'academic' },
  '4': { rung: 4, form: 'ordinal' },
  '4th': { rung: 4, form: 'ordinal' },
  '4thyear': { rung: 4, form: 'ordinal' },
  fourth: { rung: 4, form: 'ordinal' },
  fourthyear: { rung: 4, form: 'ordinal' },

  // Fifth year. Only values that say so outright land here — a redshirt senior is a senior
  // holding a fifth year, not one already in it.
  '5': { rung: 5, form: 'ordinal' },
  '5th': { rung: 5, form: 'ordinal' },
  '5thyear': { rung: 5, form: 'ordinal' },
  fifth: { rung: 5, form: 'ordinal' },
  fifthyear: { rung: 5, form: 'ordinal' },
  gr: { rung: 5, form: 'academic' },
  grad: { rung: 5, form: 'academic' },
  gs: { rung: 5, form: 'academic' },
  graduate: { rung: 5, form: 'academic' },
  gradstudent: { rung: 5, form: 'academic' },
  graduatestudent: { rung: 5, form: 'academic' },
  supersenior: { rung: 5, form: 'academic' },
};

/**
 * The redshirt prefixes a roster actually uses, longest first.
 *
 * "RS" is at least as common as "R" on a real roster and was missing entirely: normalize() turns
 * "RS Fr." into "rsfr", which matched nothing, so a quarter of the redshirts on a JUCO roster
 * read as no class at all. Stripping the prefix instead of enumerating every combination is what
 * makes "RS Fr.", "R-Fr", "RFr" and "Redshirt Freshman" one rule.
 */
const REDSHIRT_PREFIXES = ['redshirt', 'rs', 'r'] as const;

/** How many characters a remainder needs before the bare "r" prefix may claim it. */
const MIN_REMAINDER_AFTER_R = 2;

/**
 * Families that only an explicit redshirt marker may introduce.
 *
 * Bare "S" stays out of CLASS_FAMILIES because sophomore and senior both claim it. Behind a
 * redshirt marker that ambiguity does not survive contact with a real roster: a redshirt senior
 * is written "R-Sr", never "R-S". Keeping the entry here rather than in the main table is what
 * lets "R-S" read while a lone "S" cell still reads as nothing.
 */
const REDSHIRT_ONLY_FAMILIES: Record<string, ClassFamily> = {
  s: { rung: 2, form: 'academic' },
};

/**
 * Splits an optional redshirt prefix off a normalized key.
 *
 * Tries the longest prefix first and accepts the first one whose remainder is a known family, so
 * "rsoph" resolves as "r" + "soph" rather than stalling on "rs" + "oph".
 *
 * The two-character rule on the bare "r" prefix is load-bearing: without it "rf" resolves as
 * "r" + "f" and a right fielder is read as a redshirt freshman. Right field owns RF on any
 * roster that lists positions, so it must keep reading as nothing.
 */
function stripRedshirt(key: string): { base: string; isRedshirt: boolean } {
  if (CLASS_FAMILIES[key]) return { base: key, isRedshirt: false };

  for (const prefix of REDSHIRT_PREFIXES) {
    if (!key.startsWith(prefix)) continue;

    const remainder = key.slice(prefix.length);
    if (prefix === 'r' && remainder.length < MIN_REMAINDER_AFTER_R) continue;
    if (CLASS_FAMILIES[remainder]) return { base: remainder, isRedshirt: true };
  }

  return { base: key, isRedshirt: false };
}

/**
 * A redshirt marker written as its own token — "R-S", "R F", "R.J." — resolved to its family.
 *
 * THE SEPARATOR IS THE DISAMBIGUATOR. normalize() deletes it, which is what collapses the
 * compound "R-F" (redshirt + freshman) onto the single token "RF" that right field owns. Reading
 * the raw cell instead keeps those apart: a one-character remainder is trustworthy when the
 * roster itself punctuated it, and MIN_REMAINDER_AFTER_R still guards the unpunctuated "RF".
 *
 * Only consulted once the ordinary path has failed, so nothing that parses today changes.
 */
function readSeparatedRedshirt(
  value: string,
): { family: ClassFamily; isRedshirt: boolean } | null {
  const segments = value.toLowerCase().split(/[^a-z0-9]+/).filter(Boolean);
  if (segments.length < 2) return null;

  const [marker, ...rest] = segments;
  if (!(REDSHIRT_PREFIXES as readonly string[]).includes(marker)) return null;

  const remainder = rest.join('');
  const family = CLASS_FAMILIES[remainder] ?? REDSHIRT_ONLY_FAMILIES[remainder];
  return family ? { family, isRedshirt: true } : null;
}

/**
 * The year of junior college a cell names, or null when it cannot name one.
 *
 * JC counts years in the program, so a redshirt year advances a rung — the opposite of the
 * eligibility ladder. Three cases yield null rather than a plausible-looking guess:
 *
 *   - Past the third rung. There is no JC 4; a two-year school does not have one.
 *   - An academic third-year name ("Jr"). A junior on a JUCO roster is suspect data and deserves
 *     a human look, not a clamp to JC 3. An ordinal "3" is a third-year player and is fine.
 *   - A redshirt that would land past JC 3.
 */
function jucoYearFor(family: ClassFamily, isRedshirt: boolean): JucoYear | null {
  if (family.rung >= 4) return null;
  if (family.form === 'academic' && family.rung === 3) return null;

  const rung = family.rung + (isRedshirt ? 1 : 0);
  return rung <= 3 ? (String(rung) as JucoYear) : null;
}

// How many years past the current graduating class each rung sits. A senior graduates with it;
// a freshman is three classes behind. A fifth-year is graduating now too, which is what makes
// the extra year extra.
const YEARS_UNTIL_GRADUATION: Record<EligibilityClass, number> = {
  '1': 3,
  '2': 2,
  '3': 1,
  '4': 0,
  '5': 0,
};

// The same idea on a two-year ladder. A JUCO player leaves after two years, so a first-year is
// one class behind rather than three, and a second or third year is graduating now.
const JUCO_YEARS_UNTIL_GRADUATION: Record<JucoYear, number> = {
  '1': 1,
  '2': 0,
  '3': 0,
};

/**
 * The graduation year of the class currently finishing school.
 *
 * Rolls over in June rather than January, because a class is named for the summer it graduates
 * into: in August 2026 the seniors on a roster are the class of 2027, not 2026.
 */
export function getCurrentGraduatingClass(now: Date = new Date()): number {
  return now.getMonth() > 4 ? now.getFullYear() + 1 : now.getFullYear();
}

/** The graduation year a year of eligibility implies, given when we are asking. */
export function graduationYearFor(
  eligibilityClass: EligibilityClass | null | undefined,
  now: Date = new Date(),
): number | null {
  if (eligibilityClass == null) return null;
  const offset = YEARS_UNTIL_GRADUATION[eligibilityClass];
  return offset === undefined ? null : getCurrentGraduatingClass(now) + offset;
}

/**
 * The year a JUCO player finishes junior college, given when we are asking.
 *
 * A four-year ladder puts a JUCO freshman three classes out, which is wrong twice over: they are
 * not on a four-year track, and the number a coach wants is when the player can arrive. Use this
 * wherever the player is known to be JUCO; graduationYearFor is right for everyone else.
 *
 * readRosterClass does not call it, because a Class cell does not say whether the roster is JUCO.
 * The caller knows that and picks.
 */
export function jucoGraduationYearFor(
  jucoYear: JucoYear | null | undefined,
  now: Date = new Date(),
): number | null {
  if (jucoYear == null) return null;
  const offset = JUCO_YEARS_UNTIL_GRADUATION[jucoYear];
  return offset === undefined ? null : getCurrentGraduatingClass(now) + offset;
}

/**
 * Lowercases and strips everything that is not a letter or digit.
 *
 * Collapses the separator zoo — "R-FR", "R FR", "R.Fr.", "Fr." — onto one key each, so the
 * family table lists meanings rather than punctuation.
 */
function normalize(value: string): string {
  return value.toLowerCase().replace(/[^a-z0-9]/g, '');
}

/**
 * Reads one Class cell.
 *
 * The graduation year returned beside a year of eligibility is INFERRED from today, not stated
 * by the roster. It is derived so the screens that have always read a graduation year keep
 * working, but the eligibility year is the fact the file carried and should win wherever both
 * are available. A 2024 roster uploaded today reads its freshmen as the class of 2030; the
 * eligibility year is still right, which is the reason to keep it.
 *
 * A stated graduation year always wins over either ladder. A prep program sitting inside a JUCO
 * scout book lists a Grad column of calendar years, and that is a fact rather than an inference.
 */
export function readRosterClass(
  value: string | number | null | undefined,
  now: Date = new Date(),
): RosterClassReading {
  if (value == null) return EMPTY_READING;

  const trimmed = String(value).trim();
  if (!trimmed || trimmed === '-') return EMPTY_READING;

  // A stated graduation year is taken at its word and never turned into an eligibility year:
  // which year of school a 2027 recruit is in is not knowable from the year alone.
  if (GRADUATION_YEAR_PATTERN.test(trimmed)) {
    return { ...EMPTY_READING, gradYear: parseInt(trimmed, 10) };
  }

  if (TWO_DIGIT_YEAR_PATTERN.test(trimmed)) {
    return { ...EMPTY_READING, gradYear: 2000 + parseInt(trimmed, 10) };
  }

  const key = normalize(trimmed);
  if (!key) return EMPTY_READING;

  const { base, isRedshirt } = stripRedshirt(key);
  // The punctuated form is a fallback, not an alternative: anything the normalized path already
  // reads keeps reading exactly the same way.
  const resolved = CLASS_FAMILIES[base]
    ? { family: CLASS_FAMILIES[base], isRedshirt }
    : readSeparatedRedshirt(trimmed);

  if (!resolved) return yearBuriedIn(trimmed);

  // A redshirt sits on the SAME eligibility rung as the class it names, and one rung FURTHER
  // along the JC ladder. That divergence is the whole reason both are returned.
  const eligibilityClass = String(resolved.family.rung) as EligibilityClass;

  return {
    eligibilityClass,
    jucoYear: jucoYearFor(resolved.family, resolved.isRedshirt),
    gradYear: graduationYearFor(eligibilityClass, now),
  };
}

/**
 * Last resort: a year sitting inside a phrase, as in "Class of 26" or "'27".
 *
 * Only reached once the family table has had its say, so "1st" is a first year rather than the
 * year 1. The century stays pinned, so a stray 1027 still reads as nothing. This is the one
 * piece of the old digit-stripping reader worth keeping - it is how a roster that writes the
 * class of '27 in words still lands somewhere.
 */
function yearBuriedIn(value: string): RosterClassReading {
  const digits = value.replace(/\D/g, '');

  if (GRADUATION_YEAR_PATTERN.test(digits)) {
    return { ...EMPTY_READING, gradYear: parseInt(digits, 10) };
  }
  if (TWO_DIGIT_YEAR_PATTERN.test(digits)) {
    return { ...EMPTY_READING, gradYear: 2000 + parseInt(digits, 10) };
  }

  return EMPTY_READING;
}

/**
 * How a year of eligibility reads to a coach.
 *
 * The depth chart's cards show the bare number because they are a narrow column, but anywhere
 * with room says it in words. Fifth year is spelled out rather than called a super senior,
 * which is slang and not what every program calls it.
 */
const ELIGIBILITY_CLASS_LABELS: Record<EligibilityClass, string> = {
  '1': 'Freshman',
  '2': 'Sophomore',
  '3': 'Junior',
  '4': 'Senior',
  '5': 'Fifth year',
};

export function formatEligibilityClass(
  eligibilityClass: string | null | undefined,
): string | null {
  if (!eligibilityClass) return null;
  return ELIGIBILITY_CLASS_LABELS[eligibilityClass as EligibilityClass] ?? eligibilityClass;
}

/**
 * How a year of junior college reads to a coach: "JC 1", not "Freshman".
 *
 * Coaches say JC 1 out loud, and it is what the roster's own shorthand means. Spelling it as a
 * class name would collide with the eligibility ladder's labels, which is precisely the
 * confusion the two fields exist to avoid.
 */
export function formatJucoYear(jucoYear: string | null | undefined): string | null {
  if (!jucoYear) return null;
  return (JUCO_YEARS as readonly string[]).includes(jucoYear) ? `JC ${jucoYear}` : jucoYear;
}
